import { put, takeEvery, all, select, call, debounce } from 'redux-saga/effects'
import * as Actions from 'actions/journalists'
import * as AppActions from 'actions/app'
import * as Selectors from 'selectors'
import * as Api from 'api/journalists'
import * as ContactsManagementApi from 'api/contacts_management'
import { List as ImmutableList } from 'immutable'

export function* searchJournalists({ type }) {
  const i18n = yield select(Selectors.getI18n)
  try {
    const postBody = yield select(Selectors.getJournalistsSearchBody, type)
    const response = yield call(Api.search, postBody)

    yield put(Actions.searchJournalistsSuccess(response))
  } catch (error) {
    yield put(Actions.searchJournalistsError(error))

    if (error.response && error.response.statusCode === 403) {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('invalid_search_query_helper_text'),
        variant: 'error'
      }))
    } else {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('failed'),
        variant: 'error'
      }))
    }
  }
}

export function* simpleSearch() {
  yield put(Actions.searchJournalistsStart({ type: 'search' }))
}

export function* selectAllJournalists() {
  const journalists = yield select(Selectors.getJoDaJournalists)
  const identities = journalists.get('items').flatMap(j => j.get('identities'))
  const identityIds = identities.map(identity => identity.get('id'))
  const currentSelectedJournalistIds = yield select(Selectors.getJoUiSelectedJournalistIds)
  const newSelectedJournalistIds = currentSelectedJournalistIds.concat(identityIds).toSet().toList()

  yield put(Actions.setSelectedJournalistIds(newSelectedJournalistIds))
}

export function* deselectAllJournalists() {
  const journalists = yield select(Selectors.getJoDaJournalists)
  const identities = journalists.get('items').flatMap(j => j.get('identities'))
  const identityIds = identities.map(identity => identity.get('id'))
  const currentSelectedJournalistIds = yield select(Selectors.getJoUiSelectedJournalistIds)
  const newSelectedJournalistIds = currentSelectedJournalistIds.filter(id => !identityIds.includes(id))

  yield put(Actions.setSelectedJournalistIds(newSelectedJournalistIds))
}

export function* selectAllMyJournalists() {
  const journalists = yield select(Selectors.getJoDaMyContacts)
  const identities = journalists.get('items').flatMap(j => j.get('identities'))
  const identityIds = identities.map(identity => identity.get('id'))
  const currentSelectedJournalistIds = yield select(Selectors.getJoUiSelectedJournalistIds)
  const newSelectedJournalistIds = currentSelectedJournalistIds.concat(identityIds).toSet().toList()

  yield put(Actions.setSelectedJournalistIds(newSelectedJournalistIds))
}

export function* deselectAllMyJournalists() {
  const journalists = yield select(Selectors.getJoDaMyContacts)
  const identities = journalists.get('items').flatMap(j => j.get('identities'))
  const identityIds = identities.map(identity => identity.get('id'))
  const currentSelectedJournalistIds = yield select(Selectors.getJoUiSelectedJournalistIds)
  const newSelectedJournalistIds = currentSelectedJournalistIds.filter(id => !identityIds.includes(id))

  yield put(Actions.setSelectedJournalistIds(newSelectedJournalistIds))
}

export function* handleWhitePageOnImport() {
  const journalists = yield select(Selectors.getJoDaJournalists)

  if (journalists.get('items').isEmpty()) {
    const pagination = yield select(Selectors.getJournalistPagination)

    // If the current page is not full anymore after removing the last item and there are more items, rerun search
    if (pagination.get('page') * pagination.get('size') < journalists.get('total')) {
      yield put(Actions.searchJournalistsStart({ type: 'search' }))
    }
  }
}

export function* addJournalistToList() {
  const i18n = yield select(Selectors.getI18n)

  try {
    const ids = yield select(Selectors.getJoUiDistributionListsDialogJoIds)
    const selectedDistributionLists = yield select(Selectors.getJoUiSelectedDistributionLists)
    const distributionListIds = selectedDistributionLists.map(dl => dl.get('id'))
    const newDistributionLists = yield select(Selectors.getJoUiNewDistributionLists)

    yield put(Actions.setImportingJournalistsIds(ids))

    const postBody = {
      ids: ids.toJS(),
      distribution_list_ids: distributionListIds.toJS(),
      new_distribution_lists: newDistributionLists.toJS()
    }

    const response = yield call(Api.importJournalists, postBody)

    yield put(Actions.importJournalistsToListSuccess(response))
    yield put(Actions.closeDistributionListsDialog())
    yield call(handleWhitePageOnImport)

    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'info'
    }))
  } catch (error) {
    yield put(Actions.importJournalistsToListError(error))

    if (error.response.statusCode === 403) {
      yield put(AppActions.showAppSnackbarMessage({
        text: error.response.message || i18n.get('forbidden'),
        variant: 'error'
      }))
    } else {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('failed'),
        variant: 'error'
      }))
    }
  } finally {
    yield put(Actions.clearImportingJournalistsIds())
  }
}

export function* addJournalist({ payload: ids }) {
  const i18n = yield select(Selectors.getI18n)
  yield put(Actions.setImportingJournalistsIds(ids))

  try {
    const postBody = {
      ids
    }
    const response = yield call(Api.importJournalists, postBody)

    yield put(Actions.importJournalistsSuccess(response))
    yield call(handleWhitePageOnImport)
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('success'),
      variant: 'info'
    }))
  } catch (error) {
    yield put(Actions.importJournalistsError(error))

    if (error.response.statusCode === 403) {
      yield put(AppActions.showAppSnackbarMessage({
        text: error.response.message || i18n.get('forbidden'),
        variant: 'error'
      }))
    } else {
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('failed'),
        variant: 'error'
      }))
    }
  } finally {
    yield put(Actions.clearImportingJournalistsIds())
  }
}

export function* findNews() {
  const i18n = yield select(Selectors.getI18n)
  const journalist = yield select(Selectors.getJoUiDetailPageJournalist)
  const identityIds = (journalist.get('identities') || ImmutableList()).map(i => i.get('id'))

  if (identityIds.isEmpty()) {
    yield put(Actions.findNewsError())
  } else {
    try {
      const body = {
        size: 100,
        identity_ids: identityIds.toJS()
      }

      const response = yield call(Api.findArticles, body)

      yield put(Actions.findNewsSuccess(response))
    } catch (error) {
      yield put(Actions.findNewsError(error))
      yield put(AppActions.showAppSnackbarMessage({
        text: i18n.get('failed'),
        variant: 'error'
      }))
    }
  }
}

export function* searchDistributionLists({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const body = {
      filter: payload.filter,
      page: 1,
      limit: 10,
      sort_by: 'name',
      sort_order: 'asc'
    }

    const response = yield call(ContactsManagementApi.fetchDistributionLists, body)

    yield put(Actions.searchDistributionListsSuccess(response))
  } catch (error) {
    yield put(Actions.searchDistributionListsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* fetchPublications({ payload }) {
  const i18n = yield select(Selectors.getI18n)

  try {
    const channelIds = yield select(Selectors.getJoFiChannels)

    let name = payload

    if ([' ', '*'].indexOf(payload.slice(-1)) === -1) {
      name = `${payload}*`
    }

    const postBody = {
      name,
      channel_ids: channelIds,
      size: 50
    }
    const response = yield call(Api.findPublications, postBody)

    yield put(Actions.fetchPublicationsSuccess(response.publications))
  } catch (error) {
    yield put(Actions.fetchPublicationsError(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('failed'),
      variant: 'error'
    }))
  }
}

export function* fetchContentBasedSearchContents({ payload = {} }) {
  try {
    const response = yield call(Api.fetchRecentContentFilters)

    yield put(Actions.fetchContentBasedSearchContentsSuccess({
      ...response,
      showSelection: payload.showSelection || false
    }))
  } catch (error) {
    yield put(Actions.fetchContentBasedSearchContentsError(error))
  }
}

const hasContentBasedSearchFilters = filters => (
  (filters.iptcMediaTopicIds || []).length > 0 || (filters.languageIds || []).length > 0
)

export function* findJournalistsByContents({ payload: contentIds }) {
  try {
    const filters = yield call(Api.filtersFromContents, { content_ids: contentIds })

    if (!hasContentBasedSearchFilters(filters)) {
      yield put(Actions.findJournalistsByContentsError('no_filters'))

      return
    }

    yield put(Actions.setFilter({ key: 'iptcMediaTopics', value: filters.iptcMediaTopicIds }))
    yield put(Actions.setFilter({ key: 'languages', value: filters.languageIds }))
    yield put(Actions.setPagination({ page: 1 }))
    yield put(Actions.setMyContactsPagination({ page: 1 }))
    yield put(Actions.searchJournalistsStart({ type: 'search' }))
    yield put(Actions.findJournalistsByContentsSuccess())
  } catch (error) {
    yield put(Actions.findJournalistsByContentsError(error))
  }
}

// taker functions

export function* watchSimpleSearchStart() {
  yield takeEvery(Actions.simpleSearchStart, simpleSearch)
}

export function* watchPageAllContacts() {
  yield takeEvery(Actions.pageAllContacts, searchJournalists, { type: 'allContactsPagination' })
}

export function* watchPageMyContacts() {
  yield takeEvery(Actions.pageMyContacts, searchJournalists, { type: 'myContactsPagination' })
}

export function* watchSearchJournalistsStart() {
  yield takeEvery(Actions.searchJournalistsStart, searchJournalists, { type: 'search' })
}

export function* watchAddJournalistToListStart() {
  yield takeEvery(Actions.importJournalistsToListStart, addJournalistToList)
}

export function* watchAddJournalistStart() {
  yield takeEvery(Actions.importJournalistsStart, addJournalist)
}

export function* watchSearchDistributionListsStart() {
  yield debounce(1000, Actions.searchDistributionListsStart, searchDistributionLists)
}

export function* watchFetchPublicationsStart() {
  yield debounce(500, Actions.fetchPublicationsStart, fetchPublications)
}

export function* watchFetchContentBasedSearchContentsStart() {
  yield takeEvery(Actions.fetchContentBasedSearchContentsStart, fetchContentBasedSearchContents)
}

export function* watchFindJournalistsByContentsStart() {
  yield takeEvery(Actions.findJournalistsByContentsStart, findJournalistsByContents)
}

export function* watchSelectAllJournalists() {
  yield takeEvery(Actions.selectAllJournalists, selectAllJournalists)
}

export function* watchDeselectAllJournalists() {
  yield takeEvery(Actions.deselectAllJournalists, deselectAllJournalists)
}

export function* watchSelectAllMyJournalists() {
  yield takeEvery(Actions.selectAllMyJournalists, selectAllMyJournalists)
}

export function* watchDeselectAllMyJournalists() {
  yield takeEvery(Actions.deselectAllMyJournalists, deselectAllMyJournalists)
}

export function* watchFindNewsStart() {
  yield takeEvery(Actions.findNewsStart, findNews)
}

export default function* journalistSearchSaga() {
  yield all([
    watchPageAllContacts(),
    watchPageMyContacts(),
    watchSimpleSearchStart(),
    watchSearchJournalistsStart(),
    watchAddJournalistToListStart(),
    watchAddJournalistStart(),
    watchSearchDistributionListsStart(),
    watchFetchPublicationsStart(),
    watchFetchContentBasedSearchContentsStart(),
    watchFindJournalistsByContentsStart(),
    watchSelectAllJournalists(),
    watchDeselectAllJournalists(),
    watchSelectAllMyJournalists(),
    watchDeselectAllMyJournalists(),
    watchFindNewsStart()
  ])
}
